import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  TextField,
  Button,
  IconButton,
  useTheme,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { Hotel } from '../types/chat';
import { useTranslation } from 'react-i18next';

const ActivityReservationPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activities, setActivities] = useState<(string | { name: string; details?: string })[]>([]);
  const [expandedActivities, setExpandedActivities] = useState<Set<number>>(new Set());
  const [newActivity, setNewActivity] = useState('');
  const [newActivityDetails, setNewActivityDetails] = useState('');

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!user?.hotelId) {
        setError(t('hotelSettings.noHotelAssociated'));
        return;
      }
      const response = await api.get(`/hotels/${user.hotelId}`);
      const hotelData: Hotel = response.data;
      setActivities(hotelData.activities || []);
    } catch (err: any) {
      setError(err.response?.data?.message || t('hotelSettings.failedToFetch'));
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.hotelId, t]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const handleAddActivity = async () => {
    if (newActivity.trim()) {
      try {
        setSaving(true);
        setError(null);
        const activityObj = {
          name: newActivity.trim(),
          details: newActivityDetails.trim() || '',
        };
        const updatedActivities = [...activities, activityObj];
        setActivities(updatedActivities);
        setNewActivity('');
        setNewActivityDetails('');

        // Auto-save to API
        if (!user?.hotelId) {
          setError(t('hotelSettings.noHotelAssociated'));
          setActivities(activities); // Revert
          return;
        }

        await api.patch(`/hotels/${user.hotelId}`, { activities: updatedActivities });
        setSuccess(t('hotelSettings.savedSuccessfully'));
        setTimeout(() => setSuccess(null), 3000);
      } catch (err: any) {
        setError(err.response?.data?.message || t('hotelSettings.failedToSave'));
        console.error('Error saving activity:', err);
        // Revert the activity addition on error
        setActivities(activities);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleRemoveActivity = async (index: number) => {
    try {
      setSaving(true);
      setError(null);
      const updated = [...activities];
      updated.splice(index, 1);
      setActivities(updated);

      // Auto-save to API
      if (!user?.hotelId) {
        setError(t('hotelSettings.noHotelAssociated'));
        setActivities(activities); // Revert
        return;
      }

      await api.patch(`/hotels/${user.hotelId}`, { activities: updated });
      setSuccess(t('hotelSettings.savedSuccessfully'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || t('hotelSettings.failedToSave'));
      console.error('Error removing activity:', err);
      // Revert on error - re-fetch activities
      fetchActivities();
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (index: number) => {
    const newExpanded = new Set(expandedActivities);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedActivities(newExpanded);
  };

  // Common TextField styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
      '&:hover': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : '#cbd5e1',
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
        '& fieldset': {
          borderColor: isDark ? '#3b82f6' : '#667eea',
          borderWidth: 2,
        },
      },
      '& fieldset': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0',
        borderWidth: 1,
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500,
      color: isDark ? theme.palette.text.secondary : '#64748b',
      '&.Mui-focused': {
        color: isDark ? '#3b82f6' : '#667eea',
        fontWeight: 500,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh', 
      overflow: 'auto',
      bgcolor: theme.palette.background.default,
    }}>
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            {t('activityReservation.title')}
          </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: 2,
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2,
              borderRadius: 2,
            }} 
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Paper sx={{ 
          p: 3,
          borderRadius: 2,
          bgcolor: theme.palette.background.paper,
          boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
          mb: 3,
        }}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>{t('hotelSettings.activities')}</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                placeholder={t('hotelSettings.activityNamePlaceholder')}
                value={newActivity}
                onChange={(e) => setNewActivity(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAddActivity()}
                sx={textFieldSx}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddActivity}
                disabled={!newActivity.trim() || saving}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  minWidth: 100,
                  bgcolor: isDark ? '#1e3b8a' : '#3b82f6',
                  boxShadow: isDark ? '0 2px 8px rgba(30, 59, 138, 0.4)' : '0 2px 8px rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    bgcolor: isDark ? '#1e40af' : '#2563eb',
                    boxShadow: isDark ? '0 4px 12px rgba(30, 59, 138, 0.5)' : '0 4px 12px rgba(59, 130, 246, 0.4)',
                  },
                  '&:disabled': {
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#cbd5e1',
                    color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#94a3b8',
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {t('hotelSettings.add')}
              </Button>
            </Box>
            <TextField
              fullWidth
              placeholder={t('hotelSettings.activityDetailsPlaceholder')}
              multiline
              rows={3}
              value={newActivityDetails}
              onChange={(e) => setNewActivityDetails(e.target.value)}
              sx={textFieldSx}
            />
          </Box>
        </Paper>

        {activities.length === 0 ? (
          <Paper sx={{ 
            p: 4,
            borderRadius: 2,
            bgcolor: theme.palette.background.paper,
            boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
            textAlign: 'center',
          }}>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              {t('activityReservation.noActivitiesAvailable')}
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {activities.map((activity, index) => {
              const activityName = typeof activity === 'string' ? activity : activity.name;
              const activityDetails = typeof activity === 'string' ? '' : (activity.details || '');
              const isExpanded = expandedActivities.has(index);
              const hasDetails = !!activityDetails;

              return (
                <Card
                  key={index}
                  sx={{
                    borderRadius: 2,
                    border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
                    boxShadow: isDark ? '0 1px 3px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                    bgcolor: theme.palette.background.paper,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: isDark ? 'rgba(236, 72, 153, 0.2)' : '#fce7f3',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <LocationIcon sx={{ color: isDark ? '#f472b6' : '#ec4899', fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 600, 
                              color: theme.palette.text.primary,
                              fontSize: '1rem',
                            }}
                          >
                            {activityName}
                          </Typography>
                          {activityDetails && !isExpanded && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: theme.palette.text.secondary,
                                mt: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                              }}
                            >
                              {activityDetails}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={t('activityReservation.active')}
                          size="small"
                          sx={{
                            bgcolor: isDark ? '#14532d' : '#d1fae5',
                            color: isDark ? '#4ade80' : '#065f46',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            height: 24,
                          }}
                        />
                        {hasDetails && (
                          <Box
                            onClick={() => toggleExpand(index)}
                            sx={{
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              color: theme.palette.text.secondary,
                              '&:hover': {
                                color: theme.palette.text.primary,
                              },
                            }}
                          >
                            {isExpanded ? (
                              <ExpandLessIcon sx={{ fontSize: 20 }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: 20 }} />
                            )}
                          </Box>
                        )}
                        <IconButton
                          onClick={() => handleRemoveActivity(index)}
                          sx={{
                            color: isDark ? '#f87171' : '#ef4444',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(248, 113, 113, 0.2)' : '#fee2e2',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    {hasDetails && isExpanded && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0' }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.palette.text.secondary,
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {activityDetails}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ActivityReservationPage;

